import React, { useRef, useState } from "react";
import DOMPurify from "dompurify";
import classNames from "classnames";
import { setUser, setGlobalContextProperty } from "@jsq/observability";
import { toDbId } from "common/helpers/globalId";
import { setCSRFToken } from "common/utils/CSRFToken";
import { Card, Text, Link, toast, Button, Flex } from "@jsq/ds/core";
import { TwoFaActivationType } from "apps/common/authentication/common/utils";
import { GPConfigureTwoFA } from "apps/login/GPConfigureTwoFA";
import SetUpTwoFACard, {
  type onSubmitFnProps,
} from "apps/portal/Portal/common/AccountActivation/SetUpTwoFACard";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { VerifyTwoFAContainer } from "apps/portal/Portal/common/VerifyTwoFAContainer";
import {
  type LoginVerificationRequiredResponse,
  type LoginObject,
} from "common/types/mainTypes";
import { TwoFaType } from "common/types/mainEnums";
import { useLoginMutation, useResetPasswordRequestMutation } from "common/types/mainHooks";
import { CSSTransition } from "react-transition-group";
import { SingleSignOnCard } from "apps/common/authentication/common/SingleSignOnCard";
import { type Handle2FAInputs } from "apps/portal/Portal/common/AccountActivation/VerifyTwoFA";
import { StatusPageIncidentBanner } from "common/components/StatusPageIncidentBanner";
import RecoverCard from "../Recovery/components/RecoveryCard";
import SupportCard from "../Recovery/components/SupportCard";
import { LoginStep, UserContext } from "../common/constants";
import { type SingleSignOnCardProps } from "../common/SingleSignOnCard/types";
import styles from "../common/styles.scss";
import ForgotPasswordFormCard from "./ForgotPasswordForm";
import LoginFormCard from "./LoginForm";
import LoginLocationErrorMessage from "./LoginLocationErrorMessage";
import { setGpFavicons } from "../../../portal/Portal/common/utils";
import LoginStaffUpgradeEmail2FACard from "./LoginStaffUpgradeEmail2FACard";
import { FAQTakeover } from "./FAQTakeover";
import ForgotPasswordInformationCard from "./ForgotPasswordInformationCard";

const LOGIN_ATTEMPT_LIMIT_EXCEEDED_MESSAGE =
  "Login attempt limit exceeded. Further login attempts will be blocked for 5 minutes.  If you have forgotten " +
  "your password, please use the password reset link below.";

const MFA_ATTEMPT_LIMIT_EXCEEDED_MESSAGE =
  "MFA attempt limit exceeded. Further login attempts will be blocked for 5 minutes.  If you continue to have issues " +
  "please contact customer support.";

const UNKNOWN_ERROR_MESSAGE = "An unexpected error occurred, please try again.";

const INCORRECT_LOGIN_ERROR_TITLE = "Looks like you're in the wrong place";

type LoginFormik = {
  email: string;
  password: string;
};

export type TwoFAFormValue = {
  verificationToken: string;
  rememberTwoFa: boolean;
};

type ResetPasswordFormik = {
  email: string;
};

type LoginError = {
  title?: string;
  description: string | JSX.Element;
};

// Timing of 300ms to match the timing on the animation before switching cards
const ANIMATION_DURATION = 300;

export type Props = {
  userContext: UserContext;
  arenaDomain?: string;
  onSuccess?: () => void | Promise<void>;
  passwordWasReset?: boolean;
  path?: string;
  ssoProps?: SingleSignOnCardProps;
  setSSORequired?: React.Dispatch<React.SetStateAction<boolean>>;
  arena?: UnauthenticatedArenaObjectType;
  loginCardTitle?: string;
  showPoweredBy?: boolean;
  showArenaLoginMessage?: boolean;
  hideRecoveryCode?: boolean;
  prepopulatedUserEmail?: string;
  portalLinkedGpArenaNames?: string;
  showPortalTouCheckBox?: boolean;
  showHasManagerAccountBanner?: boolean;
  showActivationLinkBanner?: boolean;
  showHasActiveGlobalAccountBanner?: boolean;
  showContactSupportBanner?: boolean;
  splitScreen?: boolean;
  ssoRequired?: boolean;
  showFAQ?: boolean;
  secureDocumentsKey?: string;
};

// Cards enter from the left and exit on the right
const forwardTransitionClassnames = {
  enter: styles.Login__transition_forward_enter,
  enterActive: styles.Login__transition_forward_enter_active,
  exit: styles.Login__transition_forward_exit,
  exitActive: styles.Login__transition_forward_exit_active,
};

// Cards enter from the right and exit on the left
const backwardTransitionClassnames = {
  enter: styles.Login__transition_backward_enter,
  enterActive: styles.Login__transition_backward_enter_active,
  exit: styles.Login__transition_backward_exit,
  exitActive: styles.Login__transition_backward_exit_active,
};

export const Login: React.FunctionComponent<Props> = (props) => {
  setGpFavicons(props.arena?.faviconUrl16px, props.arena?.faviconUrl32px);

  const [login] = useLoginMutation();
  const [resetPasswordRequest] = useResetPasswordRequestMutation();

  const [FAQTakeoverVisible, setFAQTakeoverVisible] = useState(false);

  // Manages each login step
  const [step, setStepState] = useState(LoginStep.Login);

  // For enter and exit animations
  const nodeRef = useRef(null);
  const hasIdentifiedArena = useRef(false);
  const [animationClassnames, setAnimationClassnames] = useState(forwardTransitionClassnames);
  const [animationIn, setAnimationIn] = useState(true);
  const setStep = (action: LoginStep) => {
    setAnimationIn(false);
    setTimeout(() => {
      setStepState(action);
      setAnimationIn(true);
    }, ANIMATION_DURATION);
  };

  // For 2FA
  const [twoFaSmsLastFourDigits, setTwoFaSmsLastFourDigits] = useState("");
  const [twoFaType, setTwoFaType] = useState(TwoFaType.Sms);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [shouldClearTwoFAInput, setShouldClearTwoFAInput] = useState(false);
  const [showTwoFaAuthTooltip, setShowTwoFaAuthTooltip] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  // For staff email upgrade
  const [redirectLocation, setRedirectionLocation] = useState("");

  // 2FA GP configuration
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");

  if (props.arena && !hasIdentifiedArena.current) {
    hasIdentifiedArena.current = true;
    setGlobalContextProperty("arenaId", toDbId(props.arena.id));
    setGlobalContextProperty("arenaName", props.arena.name);
  }
  // For 2FA Configuration
  const twoFaOnSubmit = ({ state }: onSubmitFnProps) => {
    if (typeof state !== "undefined" && "phoneNumber" in state && "countryCode" in state) {
      setPhoneNumber(state?.phoneNumber);
      setCountryCode(state?.countryCode);
      setTwoFaType(TwoFaType.Sms);
      setTotpCode("");
    } else if (typeof state !== "undefined" && "isSettingUpTotp" in state) {
      setTwoFaType(TwoFaType.Totp);
      setTotpCode(state?.totpCode || "");
    }
    setStep(LoginStep.Configure2FAVerify);
  };

  /*
  For Login Flow
  This is a wrapper component that mounts individual forms.
  This parent manages the state of email,password and knows which step in the login process we are in.
  */

  const [email, setEmail] = useState(props.prepopulatedUserEmail ?? "");
  const [password, setPassword] = useState("");

  // Success Banner for password reset flow (to login after password reset request sent)
  const [showPasswordResetSuccessBanner, setShowPasswordResetSuccessBanner] = useState(
    props.passwordWasReset ?? false,
  );

  const [resetPasswordMessage, setResetPasswordMessage] = useState("");

  const [loginError, setLoginError] = useState<LoginError | undefined>(undefined);
  const [resetPasswordError, setResetPasswordError] = useState("");

  const handleLoginVerificationRequiredResponse = (
    loginData: LoginVerificationRequiredResponse,
  ) => {
    setStep(LoginStep.Confirm2FA);
    setTwoFaType(loginData.twoFaType as TwoFaType);
    setShowTwoFaAuthTooltip(loginData.hasMultipleManagers);
    if (loginData.twoFaType === "sms") {
      setTwoFaSmsLastFourDigits(loginData.twoFaSmsLastFourDigits || "");
    }
  };

  const handleLoginSubmit = async (values: LoginFormik) => {
    setLoginError(undefined);
    setShowPasswordResetSuccessBanner(false);
    setEmail(values.email);
    setPassword(values.password);

    try {
      const { data } = await login({
        variables: {
          loginInput: {
            userContext: props.userContext,
            arenaDomain: props.arenaDomain,
            email: values.email,
            password: values.password,
            secureDocumentsKey: props.secureDocumentsKey,
          },
        },
        context: { unauthenticated: true },
      });

      if (!data?.login) {
        setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
        return;
      }

      switch (data.login.__typename) {
        case "LoginObject":
          setCSRFToken(data.login.csrfToken);
          setUser({ id: toDbId(data.login.user.id).toString() });
          await props.onSuccess?.();
          setEmail("");
          setPassword("");
          break;
        case "LoginPasswordResetResponse":
          setResetPasswordMessage("An admin reset your password");
          setStep(LoginStep.ResetPasswordRequest);
          setPassword("");
          break;
        case "LoginVerificationRequiredResponse":
          handleLoginVerificationRequiredResponse(data.login);
          break;
        case "LoginConfigurationRequiredResponse":
          setStep(LoginStep.Configure2FA);
          break;
        case "LoginStaffEmailUpgradeRequiredResponse":
          setStep(LoginStep.StaffUpgradeTwoFaRequired);
          setRedirectionLocation(data.login.redirectLocation);
          break;
        case "MutationErrorResponse":
          if (data.login.errorCode === "JSQGraphQLErrorCode.LOGIN_ATTEMPT_LIMIT_EXCEEDED") {
            setLoginError({ description: LOGIN_ATTEMPT_LIMIT_EXCEEDED_MESSAGE });
          } else if (data.login.errorCode === "JSQGraphQLErrorCode.INCORRECT_LOGIN_LOCATION") {
            setLoginError({
              title: INCORRECT_LOGIN_ERROR_TITLE,
              description: <LoginLocationErrorMessage email={values.email} />,
            });
          } else if (
            data.login.errorCode === "JSQGraphQLErrorCode.MFA_ATTEMPT_LIMIT_EXCEEDED"
          ) {
            setLoginError({ description: MFA_ATTEMPT_LIMIT_EXCEEDED_MESSAGE });
          } else {
            setLoginError({ description: data.login.message });
          }
          break;
        default:
          setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
          break;
      }
    } catch (e) {
      setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
    }
  };

  const handleResetPasswordRequestSubmit = async (values: ResetPasswordFormik) => {
    setResetPasswordError("");
    setEmail(values.email);

    try {
      const { data } = await resetPasswordRequest({
        variables: {
          resetPasswordRequestInput: {
            userContext: props.userContext,
            email: values.email,
            arenaDomain: props.arenaDomain,
            redirectPath: props.path,
          },
        },
        context: { unauthenticated: true },
      });

      if (!data?.resetPasswordRequest?.result) {
        setResetPasswordError(UNKNOWN_ERROR_MESSAGE);
        return;
      }

      switch (data.resetPasswordRequest.result.__typename) {
        case "ResetPasswordRequestResponse":
          setStep(LoginStep.ResetPasswordRequestComplete);
          break;
        case "MutationErrorResponse":
          setResetPasswordError(data.resetPasswordRequest.result.message);
          break;
        default:
          setResetPasswordError(UNKNOWN_ERROR_MESSAGE);
      }
    } catch (e) {
      setResetPasswordError(UNKNOWN_ERROR_MESSAGE);
    }
  };

  const handleTwoFASubmit = async (values: TwoFAFormValue, resetForm: () => void) => {
    setShouldClearTwoFAInput(false);
    const onError = () => {
      resetForm();
      setShouldClearTwoFAInput(true);
    };

    try {
      const { data } = await login({
        variables: {
          loginInput: {
            userContext: props.userContext,
            arenaDomain: props.arenaDomain,
            email,
            password,
            // 2 following fields are 2FA specific fields
            verificationCode: values.verificationToken,
            rememberTwoFa: values.rememberTwoFa,
            secureDocumentsKey: props.secureDocumentsKey,
          },
        },
        context: { unauthenticated: true },
      });

      if (!data?.login) {
        setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
        return;
      }

      switch (data.login.__typename) {
        case "LoginObject":
          setCSRFToken(data.login.csrfToken);
          props.onSuccess?.();
          setEmail("");
          setPassword("");
          setIsRedirecting(true);
          break;
        case "LoginVerificationRequiredResponse":
          // User is unable to enter a blank verification code, but this is a fallback in case somehow they do.
          handleLoginVerificationRequiredResponse(data.login);
          onError();
          break;
        case "MutationErrorResponse":
          if (data.login.errorCode === "JSQGraphQLErrorCode.LOGIN_ATTEMPT_LIMIT_EXCEEDED") {
            setLoginError({ description: LOGIN_ATTEMPT_LIMIT_EXCEEDED_MESSAGE });
          } else if (
            data.login.errorCode === "JSQGraphQLErrorCode.MFA_ATTEMPT_LIMIT_EXCEEDED"
          ) {
            setLoginError({ description: MFA_ATTEMPT_LIMIT_EXCEEDED_MESSAGE });
          } else {
            setLoginError({ description: data.login.message });
          }
          onError();
          break;
        default:
          setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
          onError();
          break;
      }
    } catch (e) {
      setResetPasswordError(UNKNOWN_ERROR_MESSAGE);
    }
  };

  const handle2FAInputChange = ({
    code,
    setFieldValue,
    submitForm,
    codeLength,
  }: Handle2FAInputs) => {
    setFieldValue("verificationToken", code);
    if (code.length === codeLength) {
      submitForm();
    }
    setLoginError(undefined);
  };

  const handleForgotPassword = () => {
    setPassword("");
    setLoginError(undefined);
    setShowPasswordResetSuccessBanner(false);
    setStep(LoginStep.ResetPasswordRequest);
  };

  const resendCode = async () => {
    try {
      const { data } = await login({
        variables: {
          loginInput: {
            userContext: props.userContext,
            arenaDomain: props.arenaDomain,
            email,
            password,
          },
        },
        context: { unauthenticated: true },
      });

      if (!data?.login) {
        setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
        return;
      }

      switch (data.login.__typename) {
        case "LoginVerificationRequiredResponse":
          handleLoginVerificationRequiredResponse(data.login);
          // Only toast new code sent for non totp flows
          if (twoFaType !== TwoFaType.Totp) {
            toast.success(
              "We have sent a new code. If you do not receive it, please confirm that your authentication information is correct",
            );
          }
          break;
        case "MutationErrorResponse":
          if (data.login.errorCode === "JSQGraphQLErrorCode.LOGIN_ATTEMPT_LIMIT_EXCEEDED") {
            setLoginError({ description: LOGIN_ATTEMPT_LIMIT_EXCEEDED_MESSAGE });
          } else {
            setLoginError({ description: data.login.message });
          }
          break;
        default:
          setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
          break;
      }
    } catch (e) {
      setLoginError({ description: UNKNOWN_ERROR_MESSAGE });
    }
  };

  const loginMessage = () => {
    let innerHtml;
    let className;
    if (props?.arena?.signInAccessMessage) {
      innerHtml = props.arena.signInAccessMessage;
    } else {
      className = "align-center";
      innerHtml =
        "This site is restricted to current and prospective investors. For access, please contact us.";
    }

    return (
      <div
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(innerHtml),
        }}
        data-cy="login-welcome-message"
      />
    );
  };

  const handleRecoverySuccess = (loginObject: LoginObject) => {
    setCSRFToken(loginObject.csrfToken);
    props.onSuccess?.();
    setEmail("");
    setPassword("");
  };

  const handleGoBackFromRecovery = async () => {
    setStep(LoginStep.Confirm2FA);
    // Only need to resend new code for non totp flows
    if (twoFaType !== TwoFaType.Totp) {
      await resendCode();
    }
  };

  const handleRecoveryLinkClick = !props.hideRecoveryCode
    ? () => {
        setStep(LoginStep.Recover2FA);
      }
    : undefined;

  if (props.ssoRequired && props.ssoProps) {
    return (
      // div required so this is considered a single flex element and sign-in
      // link isn't spaced at the bottom of the page
      <div>
        <SingleSignOnCard
          arena={props.arena?.name}
          provider={props.ssoProps?.provider || "custom"}
          providerName={props.ssoProps?.providerName || "SAML"}
          onSignIn={props.ssoProps?.onSignIn}
        />
        <Text variant="secondary-md" className={styles.Login__adminSignIn} align="center">
          Workspace authentication admins can{" "}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            to="#"
            onClick={() => props.setSSORequired?.(false)}
            isInline
            data-cy="admin-bypass-link"
          >
            sign in here
          </Link>
        </Text>
      </div>
    );
  }

  return (
    <>
      {FAQTakeoverVisible && <FAQTakeover onExit={() => setFAQTakeoverVisible(false)} />}
      <CSSTransition
        noderef={nodeRef}
        in={animationIn}
        timeout={ANIMATION_DURATION}
        classNames={animationClassnames}
        onEntered={() => {
          if (step === LoginStep.ResetPasswordRequest || step === LoginStep.ContactSupport) {
            setAnimationClassnames(backwardTransitionClassnames);
          } else {
            setAnimationClassnames(forwardTransitionClassnames);
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div ref={nodeRef}>
          <Flex flexDirection="column" alignItems="center" style={{ width: "100%" }}>
            {step === LoginStep.Login && (
              <>
                {props.userContext === UserContext.Arena && <StatusPageIncidentBanner />}
                <LoginFormCard
                  handleLoginSubmit={handleLoginSubmit}
                  email={email}
                  password={password}
                  error={loginError}
                  onForgotPassword={handleForgotPassword}
                  showPasswordResetSuccessBanner={showPasswordResetSuccessBanner}
                  showHasManagerAccountBanner={props.showHasManagerAccountBanner}
                  showActivationLinkBanner={props.showActivationLinkBanner}
                  showHasActiveGlobalAccountBanner={props.showHasActiveGlobalAccountBanner}
                  showContactSupportBanner={props.showContactSupportBanner}
                  showPortalTouCheckBox={props.showPortalTouCheckBox}
                  arena={props.arena}
                  loginCardTitle={props.loginCardTitle}
                  ssoProps={props.ssoProps}
                  showPoweredBy={props.showPoweredBy}
                  portalLinkedGpArenaNames={props.portalLinkedGpArenaNames}
                  userContext={props.userContext}
                />
              </>
            )}
            {step === LoginStep.ResetPasswordRequest && (
              <ForgotPasswordFormCard
                handleResetPasswordRequestSubmit={handleResetPasswordRequestSubmit}
                error={resetPasswordError}
                email={email}
                message={resetPasswordMessage}
                arena={props.arena}
                showPoweredByHeader={!!props.showPoweredBy}
                onClickBack={() => setStep(LoginStep.Login)}
                userContext={props.userContext}
              />
            )}
            {step === LoginStep.ResetPasswordRequestComplete && (
              <ForgotPasswordInformationCard
                error={resetPasswordError}
                email={email}
                message={resetPasswordMessage}
                arena={props.arena}
                showPoweredByHeader={!!props.showPoweredBy}
                onClickBack={() => setStep(LoginStep.ResetPasswordRequest)}
                userContext={props.userContext}
                onPasswordResetFAQClick={() => setFAQTakeoverVisible(true)}
              />
            )}
            {step === LoginStep.Configure2FA && (
              <SetUpTwoFACard
                arena={props.arena}
                onSubmit={twoFaOnSubmit}
                activationType={TwoFaActivationType.GpUser}
                isSplitScreen={props.splitScreen}
                twoFaMethodOverride={[TwoFaType.Totp, TwoFaType.Sms]}
                emailAddress={email}
                password={password}
              />
            )}
            {step === LoginStep.Configure2FAVerify && (
              <GPConfigureTwoFA
                arena={props.arena}
                onSuccess={props.onSuccess}
                onBackButtonClick={() => setStep(LoginStep.Configure2FA)}
                emailAddress={email}
                password={password}
                phoneNumber={phoneNumber}
                countryCode={countryCode}
                splitScreen={props.splitScreen}
                twoFaType={twoFaType}
                totpCode={totpCode}
              />
            )}
            {step === LoginStep.Confirm2FA && (
              <Card
                className={classNames(
                  styles.Formcard__main,
                  props.splitScreen && styles.Formcard__main__split,
                )}
              >
                <VerifyTwoFAContainer
                  twoFAInputErrorMessage={(loginError?.description as string) || ""}
                  shouldClearTwoFAInput={shouldClearTwoFAInput}
                  showCheckbox
                  isComponentLoading={false}
                  isButtonLoading={isRedirecting}
                  handleSubmit={handleTwoFASubmit}
                  handleInputChange={handle2FAInputChange}
                  sendCode={resendCode}
                  twoFaSmsLastFourDigits={twoFaSmsLastFourDigits}
                  twoFaType={twoFaType}
                  arena={props.arena}
                  onRecoveryLinkClick={handleRecoveryLinkClick}
                  showPoweredBy={props.showPoweredBy}
                  showAuthCodeTooltip={showTwoFaAuthTooltip}
                />
              </Card>
            )}
            {step === LoginStep.Recover2FA && (
              <RecoverCard
                arena={props.arena}
                onRecoverySuccess={handleRecoverySuccess}
                onGoBackClick={handleGoBackFromRecovery}
                onDoNotHaveRecoveryCodeClick={() => setStep(LoginStep.ContactSupport)}
                userContext={props.userContext}
                email={email}
                password={password}
              />
            )}
            {step === LoginStep.StaffUpgradeTwoFaRequired && (
              <LoginStaffUpgradeEmail2FACard
                arenaName={props?.arena?.name}
                redirectLocation={redirectLocation}
              />
            )}
            {step === LoginStep.ContactSupport && (
              <SupportCard
                arenaName={props?.arena?.name}
                onGoBackClick={() => setStep(LoginStep.Login)}
              />
            )}
            {props.showFAQ && (
              <Flex flexDirection="row" justifyContent="center" style={{ marginTop: "8px" }}>
                <Button
                  variant="link"
                  onClick={() => setFAQTakeoverVisible(true)}
                  data-testid="login-faq-button"
                >
                  Need help signing in?
                </Button>
              </Flex>
            )}
          </Flex>

          {props?.showArenaLoginMessage && step === LoginStep.Login && (
            <div className={classNames(styles.Login__welcomeMessage, "text-md secondary")}>
              {loginMessage()}
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  );
};
